import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { LocaleProvider, getMessages, useLocale } from "@/i18n";
import { applyInquiryWording } from "@/i18n/inquiryWording";
import { api } from "@/lib/api";
import type { OnlineBookingMode, PublicBookingConfig } from "@/types";

const BookingLanguageContext = createContext<OnlineBookingMode>("reservation");

export function useBookingLanguage(): OnlineBookingMode {
  return useContext(BookingLanguageContext);
}

function useResolvedOnlineBookingMode(): OnlineBookingMode {
  const [mode, setMode] = useState<OnlineBookingMode>("inquiry");

  useEffect(() => {
    let cancelled = false;
    void api
      .get<PublicBookingConfig>("/api/v1/public/booking/config")
      .then((config) => {
        if (!cancelled) {
          setMode(config.online_booking_mode === "reservation" ? "reservation" : "inquiry");
        }
      })
      .catch(() => {
        if (!cancelled) setMode("inquiry");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return mode;
}

/** Schaltet Gasttexte auf Anfrage-Sprache, solange die Strecke nicht auf Reservierung steht. */
export function InquiryLanguageScope({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const mode = useResolvedOnlineBookingMode();
  const messages = useMemo(
    () =>
      mode === "reservation" ? getMessages(locale) : applyInquiryWording(getMessages(locale)),
    [locale, mode],
  );

  return (
    <BookingLanguageContext.Provider value={mode}>
      <LocaleProvider locale={locale} messages={messages}>
        {children}
      </LocaleProvider>
    </BookingLanguageContext.Provider>
  );
}
